import { DateTime } from 'luxon';
import { Instance, applySnapshot, flow, getParent, types } from 'mobx-state-tree';

import {
	getStudent,
	multiTransferStudent,
	removeStudent,
	saveStudent,
	transferStudent,
} from '@common/api/students';
import { handleResponseErrors } from '@common/helpers/handleResponseErrors';
import CgClass from '@common/models/CgClasses/CgClass';
import CgSchool from '@common/models/CgSchools/CgSchool';
import ConsultationProfileModel from '@common/models/Consultations/ConsultationProfile';
import CgCoordinator from '@common/models/Users/CgCoordinators/CgCoordinator';
import CgParent from '@common/models/Users/CgParents/CgParent';
import Parent from '@common/models/Users/Parents/Parent';
import User from '@common/models/Users/User';

const Test = types.model('Test', {
	id: types.identifierNumber,
	name: types.maybeNull(types.string),
	rank: types.maybeNull(types.string),
	started_at: types.maybeNull(types.string),
});

// @ts-ignore
const Student = types
	.compose(
		User,
		types.model({
			phone: types.optional(types.string, ''),
			first_name: types.optional(types.maybeNull(types.string), ''),
			last_name: types.optional(types.maybeNull(types.string), ''),
			middle_name_ua: types.optional(types.maybeNull(types.string), ''),
			middle_name: types.optional(types.maybeNull(types.string), ''),
			nationality: types.optional(types.maybeNull(types.string), ''),
			gender: types.maybeNull(types.enumeration(['m', 'f'])),
			birthday_date: types.optional(types.maybeNull(types.string), ''),
			birth_certificate: types.optional(types.maybeNull(types.string), ''),
			first_name_ua: types.optional(types.maybeNull(types.string), ''),
			last_name_ua: types.optional(types.maybeNull(types.string), ''),
			link_to_deal: types.optional(types.string, ''),
			consultation: types.maybeNull(ConsultationProfileModel),
			parents_ids: types.optional(types.array(types.number), []),
			// @ts-ignore
			parents: types.optional(types.array(types.late(() => Parent)), []),
			cg_parents_ids: types.optional(types.array(types.number), []),
			cg_parents: types.optional(types.array(types.late(() => CgParent)), []),
			cg_login_token: types.maybeNull(types.string),
			cg_school_id: types.maybeNull(types.number),
			cg_school: types.maybeNull(CgSchool),
			cg_class_id: types.maybeNull(types.number),
			cg_class: types.maybeNull(CgClass),
			cg_coordinator_id: types.maybeNull(types.number),
			cg_coordinator: types.maybeNull(CgCoordinator),
			groups: types.optional(types.array(types.frozen()), []),
			chatJWT: types.optional(types.string, ''),
			tests: types.maybeNull(types.optional(types.array(Test), [])),
			invite_token: types.maybe(types.maybeNull(types.string)),
			tags: types.optional(types.array(types.string), []),
		}),
	)
	.named('Student')
	.views((self) => ({
		get birthdayDate() {
			return self.birthday_date ? DateTime.fromISO(self.birthday_date, { zone: 'local' }) : null;
		},
	}))
	.preProcessSnapshot((snapshot) => ({
		...snapshot,
		phone: snapshot?.phone || '',
		link_to_deal: snapshot?.link_to_deal || '',
		parents_ids: snapshot?.parents_ids || [],
		cg_parents_ids: snapshot?.cg_parents_ids || [],
		cg_parents: snapshot?.cg_parents || [],
		chatJWT: snapshot?.chatJWT || '',
	}))
	.actions(() => ({
		_fetch: getStudent,
		_remove: removeStudent,
	}))
	.actions((self) => ({
		save: flow(function* ({ avatar_url, ...data }: any) {
			self.reloading = true;
			const formateData = {
				...data,
				avatar_url: data.avatar?.url || avatar_url,
				locations_ids: data.locations_ids.map((location: any) => location?.id || location),
				services_ids: data.services_ids.map((service: any) => service?.id || service),
				birthday_date: data?.birthday_date
					? DateTime.fromISO(data?.birthday_date).toFormat('yyyy-MM-dd')
					: undefined,
			};
			try {
				applySnapshot(self, formateData);
				const result = yield saveStudent(self.id, formateData);
				applySnapshot(self, result);
			} finally {
				self.reloading = false;
			}
		}),
		transfer: flow(function* (data) {
			try {
				self.loading = true;
				yield transferStudent(self.id, data);
				if (data.service_id) {
					const parent: any = getParent(self, 2);
					applySnapshot(
						parent.students,
						parent.students.filter((student: IStudentModel) => student.id !== self.id),
					);
				}
			} finally {
				self && (self.loading = false);
			}
		}),
		multiTransfer: flow(function* (data) {
			try {
				self.loading = true;
				const result = yield multiTransferStudent(self.id, data);
				handleResponseErrors(result);

				if (data[0]?.service_id) {
					const parent: any = getParent(self, 2);
					applySnapshot(
						parent.students,
						parent.students.filter((student: IStudentModel) => student.id !== self.id),
					);
				}
			} catch (e: any) {
				console.error(e);
				throw e;
			} finally {
				if (self) {
					self.loading = false;
				}
			}
		}),
	}));

// @ts-ignore
export default Student;

export interface IStudentModel extends Instance<typeof Student> {}
